import React, { createContext, useContext, useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app from "../firebase";
import { validateActiveAccount } from "../authStatus";
import { useLocation } from "react-router-dom";
import {
  ROLE_SCOPES,
  getRoleAuth,
  getRoleScopeForRole,
  logoutRole,
  subscribeRoleEvents
} from "../auth/roleSession";

const SessionContext = createContext();

function getRolesForPath(pathname = window.location.pathname) {
  const path = pathname.toLowerCase();
  if (path.startsWith("/registar")) return [ROLE_SCOPES.REGISTRAR];
  if (path.startsWith("/admission")) return [ROLE_SCOPES.ADMISSION];
  if (path.startsWith("/finances")) return [ROLE_SCOPES.FINANCE];
  if (path.startsWith("/room-management")) return [ROLE_SCOPES.ROOM_MANAGER];
  if (path.startsWith("/admin")) return [ROLE_SCOPES.SYSTEM_ADMINISTRATOR];
  if (path.startsWith("/dean")) return [ROLE_SCOPES.DEAN];
  if (path.startsWith("/program-head")) return [ROLE_SCOPES.PROGRAM_HEAD];
  if (path.startsWith("/professor")) return [ROLE_SCOPES.PROFESSOR];
  if (path.startsWith("/vp")) return [ROLE_SCOPES.VICE_PRESIDENT];
  if (path.startsWith("/signin") || path.startsWith("/student-portal")) return [ROLE_SCOPES.NEW_STUDENT];
  if (path.startsWith("/enrolled")) return [ROLE_SCOPES.ENROLLED_STUDENT];
  if (path.startsWith("/services")) {
    return [ROLE_SCOPES.REGISTRAR, ROLE_SCOPES.ADMISSION, ROLE_SCOPES.FINANCE, ROLE_SCOPES.ROOM_MANAGER];
  }
  if (path.startsWith("/academics")) {
    return [ROLE_SCOPES.DEAN, ROLE_SCOPES.PROGRAM_HEAD, ROLE_SCOPES.PROFESSOR, ROLE_SCOPES.VICE_PRESIDENT];
  }
  return [ROLE_SCOPES.DEAN, ROLE_SCOPES.PROGRAM_HEAD, ROLE_SCOPES.PROFESSOR, ROLE_SCOPES.VICE_PRESIDENT];
}

export function SessionProvider({ children }) {
  const [roleUsers, setRoleUsers] = useState({});
  const [roleRoles, setRoleRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [roleLoadingByRole, setRoleLoadingByRole] = useState({});

  const { pathname } = useLocation();
  const rolesForPath = getRolesForPath(pathname);
  const activeRole = rolesForPath.find((role) => roleUsers[role]) || rolesForPath[0];

  const currentUser = roleUsers[activeRole] || null;
  const userRole = roleRoles[activeRole] || null;

  useEffect(() => {
    let active = true;
    const db = getFirestore(app);
    const authRoles = Object.values(ROLE_SCOPES).filter((role) => role !== ROLE_SCOPES.ENROLLED_STUDENT);
    const initializedRoles = new Set();

    const handleAuthState = async (user, role) => {
      const auth = getRoleAuth(role);
      setRoleLoadingByRole((state) => ({ ...state, [role]: true }));

      if (!user) {
        setRoleUsers((state) => ({ ...state, [role]: null }));
        setRoleRoles((state) => ({ ...state, [role]: null }));
        setRoleLoadingByRole((state) => ({ ...state, [role]: false }));
        return;
      }

      setRoleUsers((state) => ({ ...state, [role]: user }));

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));

        if (userDoc.exists()) {
          const validation = validateActiveAccount(userDoc.data(), "Account");
          if (!validation.active) {
            console.warn("Inactive account detected during session bootstrap", { uid: user.uid, message: validation.message });
            await logoutRole(role);
            setRoleUsers((state) => ({ ...state, [role]: null }));
            setRoleRoles((state) => ({ ...state, [role]: null }));
            return;
          }
          const actualRole = userDoc.data()?.role || null;
          if (getRoleScopeForRole(actualRole) !== role) {
            await logoutRole(role);
            setRoleUsers((state) => ({ ...state, [role]: null }));
            setRoleRoles((state) => ({ ...state, [role]: null }));
            return;
          }
          setRoleRoles((state) => ({ ...state, [role]: actualRole }));
        } else {
          setRoleRoles((state) => ({ ...state, [role]: null }));
        }
      } catch (err) {
        console.error("Failed to load user role:", err);
        setRoleRoles((state) => ({ ...state, [role]: null }));
      } finally {
        if (active) setRoleLoadingByRole((state) => ({ ...state, [role]: false }));
      }
    };

    const markInitialized = (role) => {
      initializedRoles.add(role);
      if (active && initializedRoles.size === authRoles.length) setLoading(false);
    };

    const unsubscribers = authRoles.map((role) => {
      const auth = getRoleAuth(role);
      const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
        if (!active) return;
        await handleAuthState(user, role);
        markInitialized(role);
      });
      const unsubscribeEvents = subscribeRoleEvents(role, async (event) => {
        if (event.type === "logout") {
          await signOut(getRoleAuth(role));
          await handleAuthState(null, role);
        }
      });
      return () => {
        unsubscribeAuth();
        unsubscribeEvents();
      };
    });

    if (authRoles.length === 0) setLoading(false);

    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  const logout = async () => {
    try {
      if (!activeRole || activeRole === ROLE_SCOPES.ENROLLED_STUDENT) return;
      await logoutRole(activeRole);
      setRoleUsers((state) => ({ ...state, [activeRole]: null }));
      setRoleRoles((state) => ({ ...state, [activeRole]: null }));
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const value = {
    currentUser,
    userRole,
    setUserRole: (nextRole) => setRoleRoles((state) => ({ ...state, [activeRole]: nextRole })),
    roleLoading: loading || !!roleLoadingByRole[activeRole],
    logout,
    loading,
    isAuthenticated: !!currentUser,
    activeRole,
    roleUsers,
    roleRoles,
    roleAuth: activeRole ? getRoleAuth(activeRole) : null,
    academicsUser: roleUsers[ROLE_SCOPES.DEAN] || roleUsers[ROLE_SCOPES.PROGRAM_HEAD] || roleUsers[ROLE_SCOPES.PROFESSOR] || roleUsers[ROLE_SCOPES.VICE_PRESIDENT] || null,
    studentUser: roleUsers[ROLE_SCOPES.NEW_STUDENT] || null,
    serviceUser: roleUsers[ROLE_SCOPES.REGISTRAR] || roleUsers[ROLE_SCOPES.ADMISSION] || roleUsers[ROLE_SCOPES.FINANCE] || roleUsers[ROLE_SCOPES.ROOM_MANAGER] || roleUsers[ROLE_SCOPES.SYSTEM_ADMINISTRATOR] || null,
    academicsRole: roleRoles[ROLE_SCOPES.DEAN] || roleRoles[ROLE_SCOPES.PROGRAM_HEAD] || roleRoles[ROLE_SCOPES.PROFESSOR] || roleRoles[ROLE_SCOPES.VICE_PRESIDENT] || null,
    studentRole: roleRoles[ROLE_SCOPES.NEW_STUDENT] || null,
    serviceRole: roleRoles[ROLE_SCOPES.REGISTRAR] || roleRoles[ROLE_SCOPES.ADMISSION] || roleRoles[ROLE_SCOPES.FINANCE] || roleRoles[ROLE_SCOPES.ROOM_MANAGER] || roleRoles[ROLE_SCOPES.SYSTEM_ADMINISTRATOR] || null
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
}
